import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions,
  Textarea,
  Divider,
  Link,
} from '@mui/joy';
import { Link as RouterLink } from 'react-router-dom';
import { PageHelpButton } from '../../components/knowledge-base/PageHelpButton';
import {
  Calendar as CalendarIcon,
  User as PersonIcon,
  MapPin as LocationIcon,
  Clock as TimeIcon,
  XCircle as CancelIcon,
  Envelope as EmailIcon,
  Phone as PhoneIcon,
} from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { schedulingApi } from '../../lib/apiScheduling';
import type { ScheduledMeeting } from '../../types/scheduling';

export const MyMeetingsPage: React.FC = () => {
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [meetingToCancel, setMeetingToCancel] = useState<ScheduledMeeting | null>(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [alertMessage, setAlertMessage] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);
  const queryClient = useQueryClient();

  // Fetch upcoming meetings
  const { data: upcomingMeetings = [], isLoading } = useQuery({
    queryKey: ['upcomingMeetings'],
    queryFn: schedulingApi.getUpcomingMeetings,
  });

  // Cancel meeting mutation
  const cancelMutation = useMutation({
    mutationFn: ({ meetingId, reason }: { meetingId: string; reason?: string }) =>
      schedulingApi.cancelMeeting(meetingId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['upcomingMeetings'] });
      setCancelModalOpen(false);
      setMeetingToCancel(null);
      setCancellationReason('');
      setAlertMessage({
        type: 'success',
        message: 'Meeting cancelled successfully. Invitee has been notified.',
      });
    },
    onError: () => {
      setAlertMessage({
        type: 'danger',
        message: 'Failed to cancel meeting',
      });
    },
  });

  const handleCancelClick = (meeting: ScheduledMeeting) => {
    setMeetingToCancel(meeting);
    setCancelModalOpen(true);
  };

  const handleConfirmCancel = () => {
    if (meetingToCancel) {
      cancelMutation.mutate({
        meetingId: meetingToCancel.id,
        reason: cancellationReason,
      });
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'success';
      case 'cancelled':
        return 'danger';
      case 'completed':
        return 'neutral';
      default:
        return 'primary';
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Button component={RouterLink} to="/operations/scheduling" variant="plain">Back to calendar</Button>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography level="h2">
          My Meetings
        </Typography>
        <PageHelpButton />
      </Box>
      <Typography level="body-md" sx={{ mb: 3, color: 'text.secondary' }}>
        View and manage your scheduled meetings
      </Typography>

      {alertMessage && (
        <Alert
          color={alertMessage.type}
          sx={{ mb: 3 }}
          endDecorator={
            <Button size="sm" variant="plain" onClick={() => setAlertMessage(null)}>
              ✕
            </Button>
          }
        >
          {alertMessage.message}
        </Alert>
      )}

      {upcomingMeetings.length === 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
              <CalendarIcon size={64} color="var(--joy-palette-text-secondary)" />
              <Typography level="title-lg">No Upcoming Meetings</Typography>
              <Typography level="body-sm" color="neutral">
                You don't have any scheduled meetings at the moment.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {upcomingMeetings.map((meeting) => (
            <Card key={meeting.id} variant="outlined">
              <CardContent>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                  {/* Left side - Date/Time */}
                  <Box sx={{ minWidth: 200 }}>
                    <Stack spacing={1}>
                      <Box>
                        <Typography level="body-xs" color="neutral">
                          START TIME
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CalendarIcon size={16} />
                          <Typography level="title-sm">
                            {formatDateTime(meeting.start_time)}
                          </Typography>
                        </Stack>
                      </Box>
                      <Box>
                        <Typography level="body-xs" color="neutral">
                          DURATION
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <TimeIcon size={16} />
                          <Typography level="body-sm">
                            {meeting.duration_minutes} minutes
                          </Typography>
                        </Stack>
                      </Box>
                      <Chip
                        color={getStatusColor(meeting.status)}
                        size="sm"
                        sx={{ alignSelf: 'flex-start' }}
                      >
                        {meeting.status_display}
                      </Chip>
                    </Stack>
                  </Box>

                  <Divider orientation="vertical" />

                  {/* Middle - Meeting Details */}
                  <Box sx={{ flex: 1 }}>
                    <Typography level="title-lg" sx={{ mb: 2 }}>
                      {meeting.title}
                    </Typography>

                    <Stack spacing={1.5}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PersonIcon size={18} color="var(--joy-palette-text-secondary)" />
                        <Typography level="body-sm">
                          <strong>{meeting.invitee_name}</strong>
                        </Typography>
                        {/* The booking already created (or matched) the CRM record,
                            so the rep opens it instead of retyping the invitee. */}
                        {meeting.contact && (
                          <Link
                            component={RouterLink}
                            to={`/crm/contacts/${meeting.contact}`}
                            level="body-xs"
                          >
                            View contact
                          </Link>
                        )}
                      </Stack>

                      <Stack direction="row" spacing={1} alignItems="center">
                        <EmailIcon size={18} color="var(--joy-palette-text-secondary)" />
                        <Typography level="body-sm">{meeting.invitee_email}</Typography>
                      </Stack>

                      {meeting.invitee_phone && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PhoneIcon size={18} color="var(--joy-palette-text-secondary)" />
                          <Typography level="body-sm">{meeting.invitee_phone}</Typography>
                        </Stack>
                      )}

                      {meeting.location && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <LocationIcon size={18} color="var(--joy-palette-text-secondary)" />
                          <Typography level="body-sm">{meeting.location}</Typography>
                        </Stack>
                      )}

                      {meeting.invitee_notes && (
                        <Box sx={{ mt: 1, p: 1.5, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                          <Typography level="body-xs" color="neutral" sx={{ mb: 0.5 }}>
                            NOTES FROM INVITEE:
                          </Typography>
                          <Typography level="body-sm">{meeting.invitee_notes}</Typography>
                        </Box>
                      )}

                      {meeting.meeting_type === 'team' && meeting.team_name && (
                        <Chip size="sm" variant="outlined" color="primary">
                          Team Meeting: {meeting.team_name}
                        </Chip>
                      )}
                    </Stack>
                  </Box>

                  {/* Right side - Actions */}
                  {meeting.status === 'scheduled' && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Button
                        color="danger"
                        variant="outlined"
                        startDecorator={<CancelIcon />}
                        onClick={() => handleCancelClick(meeting)}
                      >
                        Cancel Meeting
                      </Button>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Cancel Confirmation Modal */}
      <Modal open={cancelModalOpen} onClose={() => setCancelModalOpen(false)}>
        <ModalDialog>
          <ModalClose />
          <DialogTitle>Cancel Meeting</DialogTitle>
          <DialogContent>
            {meetingToCancel && (
              <>
                <Typography level="body-sm" sx={{ mb: 2 }}>
                  Are you sure you want to cancel this meeting with{' '}
                  <strong>{meetingToCancel.invitee_name}</strong>?
                </Typography>
                <Typography level="body-sm" sx={{ mb: 2 }}>
                  Scheduled for: <strong>{formatDateTime(meetingToCancel.start_time)}</strong>
                </Typography>
                <Textarea
                  placeholder="Optional: Add a reason for cancellation"
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  minRows={3}
                  sx={{ mb: 2 }}
                />
                <Alert color="warning" size="sm">
                  The invitee will be notified about this cancellation via email.
                </Alert>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" color="primary" onClick={() => setCancelModalOpen(false)}>
              Keep Meeting
            </Button>
            <Button
              color="danger"
              onClick={handleConfirmCancel}
              loading={cancelMutation.isPending}
            >
              Cancel Meeting
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Box>
  );
};
